import { FilterFnOption } from '@tanstack/react-table';
import { TCertificate } from '../../../lib/types';
import { CERTIFICATE_STATUS_ENUM } from '../../../lib/constants/dbConstants';
import { TCertificateFilters } from './CertificateFiltersModal';
import { CertificateStatusEnum, CertificateTypeEnum } from '../../../graph-queries/__generated__/graphql';
import { stepTitles } from '../../pages/CertificateMonitoring/CertificateMonitoring';
import { isStepCompleteForAllJoints } from '../../pages/CertificateMonitoring/CertificateMonitoring.util';

export const certificatesGlobalFilterFn: FilterFnOption<TCertificate> = (
  row,
  _columnId,
  { filters, searchString }: { filters: TCertificateFilters; searchString: string }
) => {
  const { original } = row;

  if (
    // Search string doesn't match, skip the rows
    searchString &&
    !JSON.stringify(original).toLowerCase().includes(searchString.toLowerCase())
  ) {
    return false;
  }

  if (filters['isolationNumber']) {
    const isolationNumber = original?.isolationNumber;
    if (
      // isolation numbers don't match, skip the rows
      filters['isolationNumber'].length > 0 &&
      !(
        isolationNumber &&
        filters.isolationNumber.some((number) =>
          isolationNumber.toLowerCase().includes(number.toLowerCase())
        )
      )
    )
      return false;
  }

  if (filters['permitNumber']) {
    const permitNumber = original?.permitNumber;
    if (
      // permit numbers don't match, skip the rows
      filters['permitNumber'].length > 0 &&
      !(
        permitNumber &&
        filters.permitNumber.some((number) =>
          permitNumber.toLowerCase().includes(number.toLowerCase())
        )
      )
    )
      return false;
  }

  if (filters['mc1']) {
    const mc1Number = original?.maintenanceMC1?.mc1Number;
    if (
      // mc1 numbers don't match, skip the rows
      filters['mc1'].length > 0 &&
      !(
        mc1Number &&
        filters['mc1'].some((number) => mc1Number.toLowerCase().includes(number.toLowerCase()))
      )
    )
      return false;
  }

  if (filters['sh1']) {
    const sh1Number = original?.maintenanceSH1?.sh1Number;
    if (
      // sh1 numbers don't match, skip the rows
      filters['sh1'].length > 0 &&
      !(
        sh1Number &&
        filters['sh1'].some((number) => sh1Number.toLowerCase().includes(number.toLowerCase()))
      )
    )
      return false;
  }

  if (filters.status) {
    const readableStatusValue =
      CERTIFICATE_STATUS_ENUM[original.status as CertificateStatusEnum] || '';
    if (
      // status doesn't match, skip the rows
      filters.status.length > 0 &&
      !(readableStatusValue && filters.status.includes(readableStatusValue))
    )
      return false;
  }

  if (filters.system) {
    const system = original?.system;
    if (
      // systems don't match, skip the rows
      filters['system'].length > 0 &&
      !(
        system &&
        filters.system.some((number) => system.toLowerCase().includes(number.toLowerCase()))
      )
    )
      return false;
  }
  if (filters.area) {
    const certificateArea = original?.area;
    if (
      // areas don't match, skip the rows
      filters['area'].length > 0 &&
      !(
        certificateArea &&
        filters.area.some((filterArea) =>
          certificateArea.toLowerCase().includes(filterArea.toLowerCase())
        )
      )
    )
      return false;
  }

  if (filters.template) {
    const template = original?.template ? 'Yes' : 'No';
    if (
      // template doesn't match, skip the rows
      filters['template'].length > 0 &&
      !(template && filters.template.includes(template))
    )
      return false;
  }

  if (filters.completedStep && filters['completedStep'].length > 0) {
    // Handle special case - when filtering by both 'Joint Tightened' and 'Joint Witnessed' from monitoring dashboard
    // Show SBT certificates with Joint Witnessed step complete, JI1 certificates with Joint Tightened step complete
    if (filters['completedStep'].includes('Joint Tightened') && filters['completedStep'].includes('Joint Witnessed')) {
      // if sbt certificate, filter by joint witnessed step
      if (original.type === CertificateTypeEnum.Sbt) {
        const isJointWitnessedComplete = isStepCompleteForAllJoints(row.original, (stepTitles.indexOf('Joint Witnessed') + 1));
        if (!isJointWitnessedComplete) {
          return false;
        }
      } else {
        // if ji1 certificate, filter by joint tightened step
        const isJointTightenedComplete = isStepCompleteForAllJoints(row.original, (stepTitles.indexOf('Joint Tightened') + 1));
        if (!isJointTightenedComplete) {
          return false;
        }
      }
    }
    // Handle special case - when filtering by all operational phase steps from monitoring dashboard
    // Show only certificates that have completed all operational steps
    else if (
      filters['completedStep'].includes('Start Up') &&
      filters['completedStep'].includes('12 HR') &&
      filters['completedStep'].includes('24 HR') &&
      filters['completedStep'].includes('36 HR') &&
      filters['completedStep'].includes('48 HR')
    ) {
      const allStepsComplete = filters['completedStep'].every((step) => {
        const stepNo = stepTitles.indexOf(step) + 1;
        return isStepCompleteForAllJoints(row.original, stepNo);
      });

      if (!allStepsComplete) {
        return false;
      }
    }
    // handle default case - single step filter from the filter modal
    else {
      const stepNo = stepTitles.indexOf(filters.completedStep[0]) + 1;
      return !!isStepCompleteForAllJoints(row.original, stepNo);
    }
  }


  return true;
};
